import React, { useState, useEffect } from 'react';
import { Input, Select, SelectOption } from '@flixbus/honeycomb-react';
import { Asterisk } from '../../Asterisk';
import { useVoucherFormContext } from '../../Context';
import { Currencies } from '../../Rules/Trips/Currencies';
import parseCurrencyInput from '../../../../helpers/parseCurrencyInput';
import styles from './valuehybrid.module.scss';

interface DiscountType {
  type: string;
  discount: number;
  credit: number;
  currency: string;
}

const ValueHybrid: React.FC = () => {
  const {
    formState: {
      payload: { discountType },
    },
    handlePayloadChange,
  } = useVoucherFormContext();

  const defaultDiscountType: DiscountType = {
    type: 'value_hybrid',
    discount: 0,
    credit: 0,
    currency: 'EUR',
  };

  const currentDiscountType: DiscountType =
    discountType?.type === 'value_hybrid'
      ? { ...defaultDiscountType, ...discountType }
      : defaultDiscountType;

  const [creditDisplayValue, setCreditDisplayValue] = useState<string>('');

  useEffect(() => {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currentDiscountType.currency,
      minimumFractionDigits: 2,
    });
    setCreditDisplayValue(formatter.format(currentDiscountType.credit));
  }, [currentDiscountType.credit, currentDiscountType.currency]);

  const handleDiscountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const parsedValue = Math.max(0, parseFloat(e.target.value) || 0);
    handlePayloadChange({
      value: { ...currentDiscountType, discount: parsedValue },
      prop: 'discountType',
    });
  };

  const handleCurrencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target;
    handlePayloadChange({
      value: { ...currentDiscountType, currency: value },
      prop: 'discountType',
    });
  };

  const handleCreditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setCreditDisplayValue(value);
  };

  const handleCreditBlur = () => {
    const numericValue = parseCurrencyInput(creditDisplayValue);
    handlePayloadChange({
      value: { ...currentDiscountType, credit: numericValue },
      prop: 'discountType',
    });
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currentDiscountType.currency,
      minimumFractionDigits: 2,
    });
    setCreditDisplayValue(formatter.format(numericValue));
  };

  const handleCreditFocus = () => {
    const numericValue = parseCurrencyInput(creditDisplayValue);
    setCreditDisplayValue(numericValue ? numericValue.toString() : '');
  };

  return (
    <>
      <Input
        name='discount'
        id='discount'
        label={
          <>
            Discount <Asterisk />
          </>
        }
        type='number'
        min='0'
        required
        value={currentDiscountType.discount}
        onChange={handleDiscountChange}
      />
      <div className={styles.double}>
        <Select
          value={currentDiscountType.currency}
          extraClasses={styles.doubleInput}
          id='currency-input'
          required
          placeholder='Currency'
          label={
            <>
              Currency <Asterisk />
            </>
          }
          onChange={handleCurrencyChange}
        >
          {Object.keys(Currencies).map(k => (
            <SelectOption key={k} value={k}>
              {Currencies[k]}
            </SelectOption>
          ))}
        </Select>
        <Input
          extraClasses={styles.doubleInput}
          name='credit'
          id='credit'
          required
          label={
            <>
              Credit <Asterisk />
            </>
          }
          type='text'
          min='0'
          value={creditDisplayValue}
          onChange={handleCreditChange}
          onBlur={handleCreditBlur}
          onFocus={handleCreditFocus}
        />
      </div>
    </>
  );
};

export default ValueHybrid;
